import { MutableRefObject, useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'

import { Box } from '@mui/material'
import { styled } from '@mui/system'
import { createUnarySpacing, unstable_getThemeValue as getThemeValue, getValue, handleBreakpoints } from '@mui/system'
import { deepmerge } from '@mui/utils'

const parseToNumber = (val) => {
  return Number(val.replace('px', ''))
}

const getStyle = (props) => {
  const { ownerState, theme } = props
  let styles = {
    width: '100%',
    display: 'flex',
    flexFlow: 'column wrap',
    alignContent: 'flex-start',
    boxSizing: 'border-box',
    '& > *': {
      boxSizing: 'border-box'
    }
  }

  let spacingValues = getThemeValue('spacing', ownerState.spacing, theme)
  spacingValues = spacingValues.spacing

  const transformer = createUnarySpacing(theme)

  const spacingStyleFromPropValue = (propValue): ReturnType<any> => {
    let spacing

    if ((typeof propValue === 'string' && !Number.isNaN(Number(propValue))) || typeof propValue === 'number') {
      const themeSpacingValue = Number(propValue)
      spacing = getValue(transformer, themeSpacingValue)
    } else {
      spacing = propValue
    }

    return {
      height: typeof spacing === 'number' ? Math.ceil(ownerState.maxColumnHeight) : `calc(${ownerState.maxColumnHeight}px)`
    }
  }

  styles = deepmerge(
    styles,
    handleBreakpoints(
      {
        theme
      },
      spacingValues,
      spacingStyleFromPropValue
    )
  )

  let columnValues = getThemeValue('breakpoint', ownerState.columns, theme)
  columnValues = columnValues.breakpoint

  const columnStyleFromPropValue = (propValue): ReturnType<any> => {
    const columnValue = Number(propValue)
    const spacing =
      (typeof spacingValues === 'string' && !Number.isNaN(Number(spacingValues))) || typeof spacingValues === 'number'
        ? getValue(transformer, Number(spacingValues))
        : '0px'
    return {
      '& > *': {
        width: `calc((100% - ${spacing} * ${columnValue - 1}) / ${columnValue})`,
        marginBottom: spacing
      },
      '& > span': {
        width: spacing
      }
    }
  }

  styles = deepmerge(
    styles,
    handleBreakpoints(
      {
        theme
      },
      columnValues,
      columnStyleFromPropValue
    )
  )

  return styles
}

type MasonryProps = {
  children: any[]
  ownerState: any
  ref: MutableRefObject<any>
}

const MasonryRoot = styled('div')<MasonryProps>((param: any): ReturnType<any> => getStyle(param))

function Masonry(props) {
  const { children, columns = 4, spacing = 1 } = props

  const masonryRef = useRef<any>()
  const [maxColumnHeight, setMaxColumnHeight] = useState<number>(0)
  const [numberOfLineBreaks, setNumberOfLineBreaks] = useState(0)

  const ownerState = { ...props, spacing, columns, maxColumnHeight }

  const handleResize = (masonryChildren) => {
    if (!masonryRef.current || !masonryChildren || masonryChildren.length === 0) {
      return
    }

    const masonry = masonryRef.current
    const masonryFirstChild = masonryRef.current.firstChild
    const parentWidth = masonry.clientWidth
    const firstChildWidth = masonryFirstChild.clientWidth

    if (parentWidth === 0 || firstChildWidth === 0) {
      return
    }

    const firstChildComputedStyle = window.getComputedStyle(masonryFirstChild)
    const firstChildMarginLeft = parseToNumber(firstChildComputedStyle.marginLeft)
    const firstChildMarginRight = parseToNumber(firstChildComputedStyle.marginRight)
    const currentNumberOfColumns = Math.round(parentWidth / (firstChildWidth + firstChildMarginLeft + firstChildMarginRight))
    const columnHeights = new Array(currentNumberOfColumns).fill(0)

    masonry.childNodes.forEach((child) => {
      if (child.nodeType !== Node.ELEMENT_NODE || child.dataset.class === 'line-break') {
        return
      }

      const childComputedStyle = window.getComputedStyle(child)
      const childMarginTop = parseToNumber(childComputedStyle.marginTop)
      const childMarginBottom = parseToNumber(childComputedStyle.marginBottom)

      const childHeight = parseToNumber(childComputedStyle.height)
        ? Math.ceil(parseToNumber(childComputedStyle.height)) + childMarginTop + childMarginBottom
        : 0

      const currentMinColumnIndex = columnHeights.indexOf(Math.min(...columnHeights))
      columnHeights[currentMinColumnIndex] += childHeight
      const order = currentMinColumnIndex + 1
      child.style.setProperty('order', order.toString())
    })
    flushSync(() => {
      setMaxColumnHeight(Number(Math.max(...columnHeights)))
      setNumberOfLineBreaks(currentNumberOfColumns > 0 ? currentNumberOfColumns - 1 : 0)
    })
  }

  const observer = useRef(typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(handleResize))
  useEffect((): ReturnType<any> => {
    const resizeObserver = observer.current

    if (resizeObserver === undefined) {
      return undefined
    }

    if (masonryRef.current) {
      masonryRef.current.childNodes.forEach((childNode) => {
        resizeObserver.observe(childNode)
      })
      resizeObserver.observe(masonryRef.current)
    }

    return () => (resizeObserver ? resizeObserver.disconnect() : {})
  }, [columns, spacing])

  const lineBreaks = new Array(numberOfLineBreaks).fill('').map((el, index) => {
    return (
      <Box
        component='span'
        key={index + 1}
        data-class='line-break'
        style={{ order: index + 1, flexBasis: '100%', margin: 0, padding: 0 }}
      />
    )
  })

  return (
    <MasonryRoot ownerState={ownerState} ref={masonryRef}>
      {children}
      {lineBreaks}
    </MasonryRoot>
  )
}

export default Masonry
